import React, { useState, useCallback } from 'react';
import {  Loader, FolderUp, Images } from 'lucide-react';
import './ImageUploadProcessor.css';
import JSZip from 'jszip';

const ImageUploadProcessor = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadType, setUploadType] = useState(null);

  const isValidImageFile = (file) => {
    const validExtensions = ['.jpg', '.jpeg', '.png'];
    return validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );
  };

  const createZipFromFiles = async (files) => {
    const zip = new JSZip();
    
    console.log(`Starting to zip ${files.length} files...`);
    let processedCount = 0;
    
    for (const file of files) {
      try {
        processedCount++;
        console.log(`[${processedCount}/${files.length}] Zipping: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
        
        const buffer = await file.arrayBuffer();
        zip.file(file.name, buffer);
        
        // Log progress every 100 files
        if (processedCount % 100 === 0) {
          console.log(`Progress: ${processedCount}/${files.length} files processed (${Math.round((processedCount/files.length) * 100)}%)`);
        }
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
        throw new Error(`Failed to process file ${file.name}: ${error.message}`);
      }
    }
    
    console.log('Creating final zip file...');
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    console.log(`Zip file created successfully. Size: ${(zipBlob.size / 1024 / 1024).toFixed(2)} MB`);
    
    return new File([zipBlob], 'upload.zip', { type: 'application/zip' });
  };

  const handleFileSelect = useCallback(async (event) => {
    setError(null);
    setSuccessMessage('');
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    try {
      // Handle ZIP file
      if (files.length === 1 && files[0].name.toLowerCase().endsWith('.zip')) {
        setSelectedFiles(files);
        setUploadType('zip');
        return;
      }

      // Handle folder/multiple files upload
      const imageFiles = files.filter(isValidImageFile);
      
      if (imageFiles.length === 0) {
        setError('No valid image files found. Please upload JPG, JPEG, or PNG files.');
        return;
      }

       if (imageFiles.length > 500) {
        setError('Maximum 500 images allowed.');
        return;
     }

      setSelectedFiles(imageFiles);
      setUploadType('images');

    } catch (err) {
      setError('Error processing files: ' + err.message);
    }
  }, []);

  const uploadFiles = async () => {
    setUploading(true);
    setError(null);
    setSuccessMessage('');

    try {
      let fileToUpload;

      if (uploadType === 'zip') {
        fileToUpload = selectedFiles[0];
      } else {
        // Create zip from images
        fileToUpload = await createZipFromFiles(selectedFiles);
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await fetch('https://bmfg-jew-api.polarissoftwaresolutions.com/upload-zip/', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setSuccessMessage(`The upload was successful. Once the processing is done, you will get an email at jswift@bernardomfg.com with the subject Processing Complete - Request ${data.request_id} with the results`);
      setSelectedFiles([]);
      setUploadType(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-container">
      <h1>Image Upload and Processing</h1>
      
      <div className="upload-area">
        <div className="upload-options">
          <div className="upload-option">
            <input
              type="file"
              accept=".zip,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              className="file-input"
              id="files-upload"
              multiple
              disabled={uploading}
            />
            <label htmlFor="files-upload" className="upload-label">
              <Images className="upload-icon" />
              <p>Select Individual Files</p>
              <p className="max-files">Upload images or a ZIP file</p>
            </label>
          </div>

          <div className="upload-option">
            <input
              type="file"
              accept=".zip,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              className="file-input"
              id="folder-upload"
              directory=""
              webkitdirectory=""
              disabled={uploading}
            />
            <label htmlFor="folder-upload" className="upload-label">
              <FolderUp className="upload-icon" />
              <p>Select Folder</p>
              <p className="max-files">Upload an entire folder of images</p>
            </label>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="selected-files">
            <p>Selected {selectedFiles.length} file(s):</p>
            {selectedFiles.slice(0, 5).map((file, index) => (
              <p key={index} className="file-name">{file.name}</p>
            ))}
            {selectedFiles.length > 5 && (
              <p className="file-name">...and {selectedFiles.length - 5} more</p>
            )}
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <div className="button-container">
          <button
            onClick={uploadFiles}
            disabled={!selectedFiles.length || uploading}
            className="action-button"
          >
            {uploading ? (
              <>
                <Loader className="spin-icon" />
                {uploadType === 'images' ? 'Creating ZIP and uploading...' : 'Uploading...'}
              </>
            ) : (
              'Upload Files'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadProcessor;
